import { apiHooks } from '../../../hooks/useRestAPIHooks';
import { TemplatingItem, ModelUtils } from '../models';
import { useCallback } from 'react';
import { useInvalidateTemplatingItems } from './useInvalidateTemplatingItems';
import { useSelectedTemplatingItem } from './useSelectedTemplatingItem';
import { KnownImpressionTypes } from '../../SubjectViewer/Modules/AITools/ImpressionEditor/ImpressionItem';

export const useEditTemplatingItemMetadata = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  const invalidateTemplatingItems = useInvalidateTemplatingItems();
  const {
    setSelectedTemplatingItemId,
    selectedTemplatingItemQuery: { data: selectedTemplatingItem },
  } = useSelectedTemplatingItem();

  const { mutateAsync } = apiHooks.useAPIMutation('PATCH /v1/ai-templates/:id');

  const editTemplatingItemMetadata = useCallback(
    async (
      templatingItem: TemplatingItem,
      {
        name,
        impressionType,
      }: { name: string; impressionType?: KnownImpressionTypes },
    ) => {
      const editedTemplatingItem = ModelUtils.withName(
        {
          ...templatingItem,
          impressionType,
        },
        name,
      );
      return mutateAsync(
        {
          type: editedTemplatingItem.type,
          id: editedTemplatingItem.id,
          path: editedTemplatingItem.path,
          ...(editedTemplatingItem.type === 'template' && {
            impressionType: editedTemplatingItem.impressionType,
          }),
        },
        {
          onSuccess: ({ template: editedTemplatingItemRaw }) => {
            const editedTemplatingItem =
              ModelUtils.templatingItemDetailFromRawData(
                editedTemplatingItemRaw,
              );
            invalidateTemplatingItems(editedTemplatingItem, templatingItem);
            if (selectedTemplatingItem?.id === templatingItem.id) {
              setSelectedTemplatingItemId(editedTemplatingItem.id);
            }
            onSuccess?.();
          },
        },
      );
    },
    [
      mutateAsync,
      invalidateTemplatingItems,
      onSuccess,
      selectedTemplatingItem,
      setSelectedTemplatingItemId,
    ],
  );

  return { editTemplatingItemMetadata };
};
